import { TextButton } from '@creditornot/cb-button';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React, { FunctionComponent, MouseEventHandler, ChangeEvent, cloneElement, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { fetchMerchant } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { fetchVenue } from 'apis/restaurant-api/merchant-admin/venue/api';
import { SearchResult } from 'apis/restaurant-api/merchant-admin/venue-search/types';
import { useDebounce, useFetch, useLocalStorageJSON } from 'common/hooks';
import { useThemeMode } from 'common/themeMode';
import { FeatureType } from 'common/types';
import venuePosIntegrationStatus from 'common/venuePosIntegrationStatus';

import SearchItemSelectorButtonV2 from './SearchItemSelectorButtonV2';
import SearchListItem from './SearchListItem';
import { SelectorSearch } from './SelectorSearch';
import { useGetEntitiesForUser } from './hooks/useGetEntitiesForUser';
import { useSearch } from './hooks/useSearch';
import { getResultTitle, getResultSubtitle, getSlugOrId } from './selectors';
import {
  CurrentText,
  Title,
  Subtitle,
  SectionHeader,
  ListItemsContainer,
  Root,
  Content,
  Loader,
  ResultsContainer,
  SearchItemSelectorButtonContainer,
} from './styles';
import { ResultType, PreviousSelection } from './types';
import { isBrandResult, isEnterpriseResult, isMerchantResult, isVenueResult } from './utils';
import { useBrandContext } from '../brand/context/BrandContext';
import { useEnterpriseContext } from '../enterprise/context/EnterpriseContext';

export type Props = {
  selectedSlug?: string;
  selectedFeature?: FeatureType;
  isCollapsed?: boolean;
  isHorizontalBar?: boolean;
};

const MAX_PREVIOUS_SELECTIONS = 5;

type ButtonProps = {
  nothingSelected: boolean;
  activeResult?: SearchResult;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  isOpen?: boolean;
};

const SearchItemSelectorButton = ({ activeResult, nothingSelected, onClick }: ButtonProps) => {
  const { themeMode } = useThemeMode();
  const title = activeResult && activeResult.value && getResultTitle(activeResult);
  const subtitle = activeResult && activeResult.value && getResultSubtitle(activeResult);
  return (
    <TextButton variant="black" onClick={onClick}>
      <CurrentText>
        {nothingSelected && <FormattedMessage id="searchItemSelector.selectVenue" />}
        <Title>{title}</Title> {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </CurrentText>
      <MagnifyingGlass fill={themeMode === 'light' ? colors.pepper64 : colors.salt72} width={16} height={16} />
    </TextButton>
  );
};

const Results: FunctionComponent<{
  closeDropdown?: () => void;
  heading: JSX.Element | string;
  results: JSX.Element[];
}> = ({ closeDropdown, heading, results }): JSX.Element | null =>
  results.length > 0 ? (
    <>
      <SectionHeader>{heading}</SectionHeader>
      <ListItemsContainer>{results.map(element => cloneElement(element, { closeDropdown }))}</ListItemsContainer>
    </>
  ) : null;

const SearchItemSelector = ({ selectedSlug, selectedFeature, isCollapsed, isHorizontalBar = false }: Props) => {
  const history = useHistory();

  const [show, setShow] = useState(false);
  const [searchString, setSearchString] = useState('');

  const [previousSelections, setPreviousSelections] = useLocalStorageJSON<PreviousSelection[]>(
    'previousSelections',
    [],
  );

  const validPreviousSelections = useMemo(
    () => previousSelections.filter(selection => !!selection.href),
    [previousSelections],
  );

  const toggleDropdown = () => setShow(!show);
  const closeDropdown = () => setShow(false);

  const { data: merchant } = useQuery(
    ['/v1/merchant-admin/merchants/', selectedSlug],
    () => (selectedSlug ? fetchMerchant(selectedSlug) : undefined),
    { enabled: selectedFeature === 'merchant' && !!selectedSlug, refetchOnWindowFocus: false },
  );

  const { data: venue } = useQuery(
    ['/v1/merchant-admin/venues/', selectedSlug],
    () => (selectedSlug ? fetchVenue(selectedSlug) : undefined),
    { enabled: selectedFeature === 'venue' && !!selectedSlug, refetchOnWindowFocus: false },
  );

  const { enterprise } = useEnterpriseContext();
  const { brand } = useBrandContext();

  const activeResult = useMemo<SearchResult | undefined>(() => {
    if (selectedFeature === 'merchant' && merchant) {
      return { type: 'merchant', value: merchant };
    }
    if (selectedFeature === 'venue' && venue) {
      return { type: 'venue', value: venue };
    }

    // Brands are a subset of enterprise
    if (selectedFeature === 'enterprise' && brand) {
      return { type: 'brand', value: brand };
    }

    if (selectedFeature === 'enterprise' && enterprise) {
      return { type: 'enterprise', value: enterprise };
    }
    return undefined;
  }, [brand, enterprise, merchant, selectedFeature, venue]);

  const nothingSelected = !selectedSlug;

  const activeSlug = getSlugOrId(activeResult);

  const { entities, isLoading: isLoadingEntities } = useGetEntitiesForUser();
  const { searchResults, mutate, isSearching } = useSearch();
  const debouncedHandleSearch = useDebounce(mutate, 300);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchString(searchValue);
    debouncedHandleSearch(searchValue);
  };

  const enterprises = useMemo(
    () =>
      searchResults.length
        ? searchResults.filter(isEnterpriseResult).map(result => result.value)
        : entities.enterprises,
    [entities.enterprises, searchResults],
  );
  const brands = useMemo(
    () => (searchResults.length ? searchResults.filter(isBrandResult).map(result => result.value) : entities.brands),
    [entities.brands, searchResults],
  );
  const merchants = useMemo(
    () =>
      searchResults.length ? searchResults.filter(isMerchantResult).map(result => result.value) : entities.merchants,
    [entities.merchants, searchResults],
  );
  const venues = useMemo(
    () => (searchResults.length ? searchResults.filter(isVenueResult).map(result => result.value) : entities.venues),
    [entities.venues, searchResults],
  );

  const onClickSearchItem = ({
    slug,
    label,
    resultType,
    href,
    secondLine,
    listimage,
  }: {
    slug: string;
    label: string;
    resultType: ResultType;
    href: string;
    secondLine?: string;
    listimage?: string;
  }) => {
    if (!validPreviousSelections.find(selection => selection.slug === slug)) {
      const newSelection = { label, slug, href, resultType, secondLine, listimage };
      setPreviousSelections([newSelection, ...validPreviousSelections].slice(0, MAX_PREVIOUS_SELECTIONS));
    }

    history.push(href);
  };

  const renderedEnterprises = enterprises.map(enterpriseItem => {
    const slug = enterpriseItem.id || '';
    const href = `/enterprise/${slug}`;

    return (
      <SearchListItem
        key={slug}
        label={enterpriseItem.name}
        slug={slug}
        isActive={slug === activeSlug}
        resultType="enterprise"
        href={href}
        onClick={onClickSearchItem}
      />
    );
  });

  const renderedBrands = brands.map(brand => {
    const { country, id: slug } = brand;
    const href = `/enterprise/${brand?.enterpriseId}/brand/${slug}`;

    return (
      <SearchListItem
        key={slug}
        label={brand.name}
        slug={slug}
        secondLine={country}
        isActive={slug === activeSlug}
        resultType="brand"
        href={href}
        onClick={onClickSearchItem}
      />
    );
  });

  const renderedMerchants = merchants.map(merchant => {
    const slug = merchant.itemid.$oid;
    const href = `/merchant/${slug}`;

    return (
      <SearchListItem
        key={slug}
        label={merchant.name[0].value}
        slug={slug}
        isActive={slug === activeSlug}
        resultType="merchant"
        href={href}
        onClick={onClickSearchItem}
      />
    );
  });

  const renderedVenues = venues.map(venue => {
    const { slug } = venue;
    const href = `/venue/${slug}`;
    const listimage = venue.listimage || undefined;

    return (
      <SearchListItem
        key={slug}
        label={venue.name[0].value}
        slug={slug}
        isActive={slug === activeSlug}
        resultType="venue"
        href={href}
        onClick={onClickSearchItem}
        secondLine={venue.address}
        listimage={listimage}
      />
    );
  });

  const renderedPreviousSelections = validPreviousSelections.reduce(
    (prev, { label, slug, resultType, href, secondLine, listimage }: PreviousSelection) => {
      return activeSlug !== slug
        ? [
            ...prev,
            <SearchListItem
              key={`previousSelection-${slug}`}
              label={label}
              slug={slug}
              isActive={false}
              resultType={resultType}
              href={href}
              onClick={onClickSearchItem}
              secondLine={secondLine}
              listimage={listimage}
            />,
          ]
        : prev;
    },
    [],
  );

  const { data: posIntegrationStatus } = useFetch(async () => {
    if (activeResult?.type !== 'venue') {
      return null;
    }
    try {
      return await venuePosIntegrationStatus(activeResult.value.id);
    } catch (e) {
      return null;
    }
  }, [activeResult?.type, activeResult?.value]);

  const isLoading = isSearching || isLoadingEntities;

  const isDaasVenueSelected = activeResult?.type === 'venue' && activeResult.value?.type === 'daas';

  const showResults =
    !!validPreviousSelections.length ||
    !!merchants.length ||
    !!venues.length ||
    !!enterprises.length ||
    !!brands.length;

  const defaultSelectorButtonProps = {
    onClick: toggleDropdown,
    isOpen: show,
    activeResult,
    nothingSelected,
  };

  return (
    <Root
      content={
        <Content>
          <SelectorSearch value={searchString} onChange={handleSearch} />
          {isLoading && <Loader />}
          {showResults && (
            <ResultsContainer>
              {!isLoading && (
                <Results
                  closeDropdown={closeDropdown}
                  heading={<FormattedMessage id="searchItemSelector.enterprises" />}
                  results={renderedEnterprises}
                />
              )}
              {!isLoading && (
                <Results
                  closeDropdown={closeDropdown}
                  heading={<FormattedMessage id="searchItemSelector.brands" />}
                  results={renderedBrands}
                />
              )}
              {!isLoading && (
                <Results
                  closeDropdown={closeDropdown}
                  heading={<FormattedMessage id="searchItemSelector.merchants" />}
                  results={renderedMerchants}
                />
              )}
              {!isLoading && (
                <Results
                  closeDropdown={closeDropdown}
                  heading={<FormattedMessage id="searchItemSelector.venues" />}
                  results={renderedVenues}
                />
              )}
              <Results
                closeDropdown={closeDropdown}
                heading={<FormattedMessage id="searchItemSelector.previousSelections" />}
                results={renderedPreviousSelections}
              />
            </ResultsContainer>
          )}
        </Content>
      }
      onClickOutside={closeDropdown}
      placement="auto"
      show={show}
      renderArrow={false}
      disableContentPadding
    >
      <SearchItemSelectorButtonContainer>
        {!isHorizontalBar ? (
          <SearchItemSelectorButtonV2
            {...defaultSelectorButtonProps}
            isCollapsed={isCollapsed}
            isDaasVenueSelected={isDaasVenueSelected}
            isPOS={posIntegrationStatus?.is_pos_integrated}
          />
        ) : (
          <SearchItemSelectorButton {...defaultSelectorButtonProps} />
        )}
      </SearchItemSelectorButtonContainer>
    </Root>
  );
};

export default SearchItemSelector;
